// ── El token que va en la URL de un fichero ──────────────────────────────────
//
// Un `<img>`, un `<video>` o una descarga no pueden mandar cabeceras, así que la
// credencial va en la URL (`?token=`). Hasta 2026-10-15 era el token de acceso
// entero: quince minutos de acceso a TODA la API en el historial, en el
// portapapeles de quien copia la dirección de una imagen y en el registro del
// proxy. Ahora es un token de medios (POST /auth/media-token): diez minutos,
// solo abre las rutas que se autentican por URL y sus permisos los dice la
// sesión al usarlo.
//
// `urlWithToken` es síncrono —se llama dentro de una plantilla— y no puede
// esperar a pedirlo. Así que se guarda uno y se renueva por detrás antes de que
// caduque; mientras no lo hay (el primer instante tras entrar, o un servidor
// sin la ruta) la URL lleva el token de acceso como siempre. Nada deja de
// cargarse por esto.

const RENEW_BEFORE_MS = 2 * 60_000
const USABLE_MARGIN_MS = 15_000

/** La sesión de un token de acceso (`sid`): un refresco no cambia de sesión. */
export function sessionOf(accessToken) {
  try {
    const body = String(accessToken).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const decode = typeof atob === 'function' ? atob : (text) => Buffer.from(text, 'base64').toString('binary')
    return JSON.parse(decode(body)).sid ?? String(accessToken)
  } catch {
    return accessToken ? String(accessToken) : null
  }
}

/**
 * @param {object} deps
 * @param {() => Promise<{token: string, expiresInSeconds: number}>} deps.fetchToken
 * @param {() => number} [deps.now]
 */
export function createMediaTokens({ fetchToken, now = () => Date.now() }) {
  let current = null   // { token, expiresAt, session }
  let pending = null
  let failedAt = 0

  function refresh(accessToken) {
    if (pending) return pending
    // Un servidor que no contesta (o sin la ruta) no se pregunta en bucle:
    // cada <img> de una galería llamaría aquí.
    if (failedAt && now() - failedAt < 60_000) return Promise.resolve(null)
    const session = sessionOf(accessToken)
    pending = Promise.resolve()
      .then(() => fetchToken())
      .then(({ token, expiresInSeconds }) => {
        current = { token, expiresAt: now() + Number(expiresInSeconds) * 1000, session }
        failedAt = 0
        return token
      })
      .catch(() => { failedAt = now(); return null })
      .finally(() => { pending = null })
    return pending
  }

  return {
    /**
     * El token para la URL: el de medios si hay uno de ESTA sesión y vigente;
     * si no, el de acceso. Pide uno nuevo por detrás cuando hace falta.
     */
    forUrl(accessToken) {
      if (!accessToken) return ''
      const session = sessionOf(accessToken)
      const valid = current && current.session === session && current.expiresAt - USABLE_MARGIN_MS > now()
      if (!valid || current.expiresAt - RENEW_BEFORE_MS <= now()) refresh(accessToken)
      return valid ? current.token : accessToken
    },
    refresh,
    clear() { current = null; failedAt = 0 },
  }
}
